import React, { useState, useEffect } from 'react'

import Skeleton from '@ynap/skeleton'
import BEMHelper from '@ynap/bem-helper'
import { ToggleSection } from '@ynap/toggle'
import { Notification } from '@ynap/notification'
import { Button, SimpleAsyncButton } from '@ynap/button'

import { OrderItem } from '../index'
import OrderItemSkeleton from '../OrderItemSkeleton/OrderItemSkeleton'
import type { OrdersSectionProps } from './types'

const bem = new BEMHelper('OrderHistory')

const OrdersSection = ({
    category,
    description,
    fetchOrderHistory,
    fetchOrderHistoryError,
    isFetchingOrderHistory,
    messages,
    orders,
    pageSize,
    showExchangeDerivedOrderIds,
}: OrdersSectionProps) => {
    const [isOpen, setIsOpen] = useState(category === 'recent' && (typeof orders.pagination.currentPage === 'undefined' || orders.pagination.totalOrdersDisplayed > 0))

    useEffect(() => {
        fetchOrderHistory(1, pageSize, category, true)
    }, [])

    const toggleIsOpen = () => {
        setIsOpen(!isOpen)
    }

    const loadMoreOrders = () => {
        const { pagination } = orders
        const pageNumber = pagination && pagination.currentPage ? pagination.currentPage + 1 : 1
        fetchOrderHistory(pageNumber, pageSize, category, pageNumber === 1)
    }

    const { items, pagination } = orders
    const { totalOrders, totalOrdersDisplayed } = pagination || {}
    const isFetchingOrders = isFetchingOrderHistory[category]
    const failedToFetchOrders = fetchOrderHistoryError[category]
    const title = messages.orderHistory[category + 'Orders']({
        totalOrders,
        error: typeof totalOrders === 'undefined' || isFetchingOrders || failedToFetchOrders,
    })
    const showSkeleton =
        !failedToFetchOrders && (typeof totalOrders == 'undefined' || (totalOrders == 0 && isFetchingOrders))

    const canOpen = !items || pagination.totalOrders > 0 || isFetchingOrders || failedToFetchOrders
    const open = canOpen && isOpen

    return (
        <ToggleSection
            className={bem(`${category}OrdersToggle`, { closed: !isOpen })}
            title={title}
            type="heading"
            description={description}
            onToggle={toggleIsOpen}
            open={open}
        >
            {!failedToFetchOrders && items ? (
                <div className={bem('pagination')}>
                    {messages.orderHistory.paginationDetails({ totalOrdersDisplayed, totalOrders })}
                </div>
            ) : null}

            {showSkeleton ? (
                <>
                    <Skeleton containerClassName={bem('skeletonStatus')} />
                    <div className={bem('orderList')}>
                        {Array(pageSize)
                            .fill('')
                            .map((_ignore, i) => (
                                <OrderItemSkeleton key={i} />
                            ))}
                    </div>
                </>
            ) : (
                <>
                    {failedToFetchOrders ? null : pagination && pagination.shouldDisplayLoadMore ? (
                        <>
                            <div className={bem('orderList')}>
                                {items &&
                                    items.map((item, i) => (
                                        <OrderItem
                                            key={item.externalOrderID ? item.externalOrderID : i}
                                            {...item}
                                            messages={messages}
                                            category={category}
                                            showExchangeDerivedOrderIds={showExchangeDerivedOrderIds}
                                        />
                                    ))}
                            </div>
                            <div className={bem('loadMoreContainer')}>
                                <SimpleAsyncButton
                                    className={bem('loadMore')}
                                    type="secondary"
                                    onClick={() => loadMoreOrders()}
                                    copy={messages.orderHistory.loadMore()}
                                    loading={isFetchingOrders}
                                />
                            </div>
                        </>
                    ) : (
                        <div className={bem('orderList')}>
                            {items &&
                                items.map((o, i) => (
                                    <OrderItem
                                        key={o.externalOrderID ? o.externalOrderID : i}
                                        {...o}
                                        messages={messages}
                                        category={category}
                                        showExchangeDerivedOrderIds={showExchangeDerivedOrderIds}
                                    />
                                ))}
                        </div>
                    )}
                </>
            )}

            {failedToFetchOrders ? (
                <Notification type="error" message={messages.orderHistory.errorLoadingMoreOrders()} inlineAction={true}>
                    <Button type="primary-link" className={bem('inlineErrorAction')} onClick={() => loadMoreOrders()}>
                        {messages.orderHistory.retryButton()}
                    </Button>
                </Notification>
            ) : null}
        </ToggleSection>
    )
}

export default OrdersSection
